import { Product, ProductVariant } from '../../../services/products/products.service';

/**
 * Promo tag that marks a product or variant as a "Deal Lovers" item.
 * The same string is applied at both the product and variant level, so matching
 * must check both (see {@link getDealLoversProducts}).
 */
export const DEAL_LOVERS_TAG = '70 Deal Lovers Prods';

const hasDealLoversTag = (tags?: string[]): boolean =>
  Array.isArray(tags) && tags.includes(DEAL_LOVERS_TAG);

/** A Deal Lovers product grouped with the variants that should be re-priced. */
export interface DealLoverProduct {
  productId: string;
  name: string;
  internalName: string;
  productCode: string;
  /** All category names defined on the product upstream (verbatim). */
  categories: string[];
  /** Variants targeted for re-pricing (union of product-level and variant-level tags). */
  targetVariants: ProductVariant[];
}

/** A category and the Deal Lovers products that belong to it. */
export interface DealLoverCategoryGroup {
  category: string;
  products: DealLoverProduct[];
}

/** Label for products that define no category upstream. */
const UNCATEGORIZED = 'Uncategorized';

/**
 * Canonical food categories in priority order. A product that declares several
 * categories upstream is placed in the highest-priority one found here, so each
 * product lands in exactly one category (no duplicate variants). Umbrella /
 * non-food categories (e.g. "Deal Lover's", "Pizzas", "Party of One") are
 * intentionally omitted — they only win when a product matches nothing here.
 * Edit this list to tune grouping.
 */
export const CATEGORY_PRIORITY = [
  'Pizza',
  'Wings',
  'Melts',
  'Pasta',
  'Drink',
  'App/Side',
  'Dessert',
];

/**
 * Picks the single category a product should be grouped under, given the
 * categories it declares upstream: the highest-priority canonical match, else
 * the first declared category (verbatim), else "Uncategorized".
 */
export const pickPrimaryCategory = (categories: string[]): string => {
  const preferred = CATEGORY_PRIORITY.find((category) => categories.includes(category));
  if (preferred) return preferred;
  return categories[0] || UNCATEGORIZED;
};

/**
 * The internal `{ minimal, enriched }` operation wrapper that the bulk-update
 * Redux flow expects (see `addPendingOp` in useBulkUpdate and `mapPatchOperations`).
 * - `minimal` is the API payload shape.
 * - `enriched` carries `__typename` + nested objects used for UI validation.
 */
export interface OperationWrapper {
  minimal: {
    setVariantPrice: {
      variantId: string;
      authoringPrice: { amount: number };
    };
  };
  enriched: {
    __typename: 'SetVariantPriceMenuPatchOperation';
    setVariantPrice: {
      variant: { variantId: string };
      authoringPrice: { amount: number };
    };
  };
}

export interface DealLoversCsvRow {
  internalName: string;
  productId: string;
  productCode: string;
  variantId: string;
  variantCode: string;
}

/**
 * Filters the full product list down to Deal Lovers products and, for each,
 * the variants that should be re-priced.
 *
 * A variant is a target when EITHER:
 * - the variant's own `promoTags` includes {@link DEAL_LOVERS_TAG}, OR
 * - its parent product's `promoTags` includes the tag (then ALL of the product's
 *   variants are targets).
 *
 * Products with no resulting target variants are omitted.
 */
export const getDealLoversProducts = (products: Product[]): DealLoverProduct[] => {
  if (!Array.isArray(products)) return [];

  return products.reduce<DealLoverProduct[]>((acc, product) => {
    const variants = product.variants || [];
    const productTagged = hasDealLoversTag(product.promoTags);

    const targetVariants = productTagged
      ? variants
      : variants.filter((variant) => hasDealLoversTag(variant.promoTags));

    if (targetVariants.length > 0) {
      const categories = (product.categories || [])
        .map((category) => category?.name)
        .filter((name): name is string => !!name);

      acc.push({
        productId: product.productId,
        name: product.name,
        internalName: product.internalName,
        productCode: product.productCode,
        categories,
        targetVariants,
      });
    }

    return acc;
  }, []);
};

/**
 * Groups Deal Lovers products into exactly one category each, chosen by
 * {@link pickPrimaryCategory} from the categories declared upstream. This
 * guarantees every variant appears under a single category (no duplicates).
 * Categories and the products within them are sorted alphabetically.
 */
export const groupDealLoversByCategory = (
  groups: DealLoverProduct[]
): DealLoverCategoryGroup[] => {
  const byCategory = new Map<string, DealLoverProduct[]>();

  groups.forEach((group) => {
    const category = pickPrimaryCategory(group.categories);
    const list = byCategory.get(category) || [];
    list.push(group);
    byCategory.set(category, list);
  });

  return Array.from(byCategory.entries())
    .map(([category, products]) => ({
      category,
      products: [...products].sort((a, b) =>
        (a.name || a.internalName).localeCompare(b.name || b.internalName)
      ),
    }))
    .sort((a, b) => a.category.localeCompare(b.category));
};

/**
 * Builds one `SetVariantPriceMenuPatchOperation` wrapper per target variant of a
 * Deal Lovers product, all set to the same price (in cents).
 */
export const buildSetVariantPriceOps = (
  group: DealLoverProduct,
  amountCents: number
): OperationWrapper[] =>
  group.targetVariants.map((variant) => ({
    minimal: {
      setVariantPrice: {
        variantId: variant.variantId,
        authoringPrice: { amount: amountCents },
      },
    },
    enriched: {
      __typename: 'SetVariantPriceMenuPatchOperation',
      setVariantPrice: {
        variant: { variantId: variant.variantId },
        authoringPrice: { amount: amountCents },
      },
    },
  }));

/**
 * Flattens Deal Lovers products into reference CSV rows (one per target variant).
 * Columns mirror the price-patcher mapping export.
 */
export const toDealLoversCsv = (groups: DealLoverProduct[]): DealLoversCsvRow[] =>
  groups.flatMap((group) =>
    group.targetVariants.map((variant) => ({
      internalName: group.internalName,
      productId: group.productId,
      productCode: group.productCode,
      variantId: variant.variantId,
      variantCode: variant.variantCode,
    }))
  );

export const DEAL_LOVERS_CSV_HEADERS = [
  { label: 'internalName', key: 'internalName' },
  { label: 'productId', key: 'productId' },
  { label: 'productCode', key: 'productCode' },
  { label: 'variantId', key: 'variantId' },
  { label: 'variantCode', key: 'variantCode' },
];

/**
 * Parses a whole-cents price string into an integer cents amount, matching how
 * the regular bulk-update form treats `authoringPrice.amount` (the entered value
 * is used verbatim — no dollars-to-cents conversion). Returns null when the
 * input is empty or not a valid non-negative whole number.
 */
export const parseCents = (value: string): number | null => {
  if (value == null) return null;
  const cleaned = value.trim();
  if (cleaned === '') return null;
  const cents = Number(cleaned);
  if (!Number.isFinite(cents) || cents < 0 || !Number.isInteger(cents)) return null;
  return cents;
};

export type PriceStatus = 'empty' | 'valid' | 'invalid';

/**
 * Classifies a price entry: 'empty' (blank — will be skipped), 'valid' (a
 * positive whole-cents number) or 'invalid' (non-empty but not a positive whole
 * number of cents, including 0 — a $0.00 deal price is not allowed; clear the
 * field to skip instead).
 */
export const getPriceStatus = (value: string): PriceStatus => {
  if (value == null || value.trim() === '') return 'empty';
  const cents = parseCents(value);
  return cents === null || cents === 0 ? 'invalid' : 'valid';
};
